import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
    OnDestroy,
    ElementRef,
    ViewChild,
    TemplateRef,
    forwardRef,
    ChangeDetectorRef, OnChanges, DoCheck, IterableDiffers,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { deepEqual, safeClone } from '@client/app/shared/utils/object-utils';

interface SelectorOption {
    [key: string]: any;
    label?: string;
    value?: any;
    group?: string;
}

interface RemoteOptions {
    [key: string]: any;
}

interface RemoteSettings {
    method?: string;
    cache?: boolean;
    params?: any;
    [key: string]: any;
}

@Component({
    selector: 'angular-selector',
    templateUrl: './angular-selector.component.html',
    styleUrls: ['./angular-selector.component.css'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AngularSelectorComponent),
            multi: true,
        },
    ],
    standalone: false,
})
export class AngularSelectorComponent implements OnInit, OnChanges, DoCheck, OnDestroy, ControlValueAccessor {
    @Input() monId?: string;
    @Input() name?: string;
    @Input() value: any;
    @Input() disabled: boolean = false;
    @Input() disableSearch: boolean = false;
    @Input() required: boolean = false;
    @Input() multiple: boolean = false;
    @Input() placeholder?: string = 'Choose an option';
    @Input() valueAttr?: string;
    @Input() labelAttr: string = 'label';
    @Input() groupAttr: string = 'group';
    @Input() options: SelectorOption[] = [];
    @Input() debounce: number = 0;
    @Input() create?: (input: { input: string }) => any;
    @Input() limit: number = Infinity;
    @Input() rtl: boolean = false;
    @Input() api: any = {};
    @Input() remote?: any;
    @Input() remoteParam: string = 'q';
    @Input() remoteValidation?: (options: RemoteOptions) => Observable<any> | RemoteSettings;
    @Input() remoteValidationParam: string = 'value';
    @Input() removeButton: boolean = true;
    @Input() softDelete: boolean = false;
    @Input() closeAfterSelection: boolean = false;
    @Input() viewItemTemplate?: string;
    @Input() dropdownItemTemplate?: string;
    @Input() dropdownCreateTemplate?: string;
    @Input() dropdownGroupTemplate?: string;
    @Input() skipModel = false;
    @Input() hideDropdownIcon = false;

    @Output() change = new EventEmitter<any>();

    @ViewChild('inputElement', { static: true }) inputElement!: ElementRef<HTMLInputElement>;
    @ViewChild('dropdown', { static: true }) dropdown!: ElementRef<HTMLUListElement>;
    @ViewChild('sceSelectTemplate', { static: true }) sceSelectTemplate!: TemplateRef<any>;
    @ViewChild('sceDropdownItemTemplate', { static: true }) sceDropdownItemTemplate!: TemplateRef<any>;
    @ViewChild('profileSelectItemTemplate', { static: true }) profileSelectItemTemplate!: TemplateRef<any>;
    @ViewChild('profileSelectTemplate', { static: true }) profileSelectTemplate!: TemplateRef<any>;
    @ViewChild('profileTemplate', { static: true }) profileTemplate!: TemplateRef<any>;
    @ViewChild('sceDropdownTemplate', { static: true }) sceDropdownTemplate!: TemplateRef<any>;
    @ViewChild('domainDropdownItemTemplate', { static: true }) domainDropdownItemTemplate!: TemplateRef<any>;
    @ViewChild('domainSelectTemplate', { static: true }) domainSelectTemplate!: TemplateRef<any>;
    @ViewChild('moduleSelectTemplate', { static: true }) moduleSelectTemplate!: TemplateRef<any>;
    @ViewChild('shortcutDropdownItemTemplate', { static: true }) shortcutDropdownItemTemplate!: TemplateRef<any>;
    @ViewChild('shortcutDropdownViewTemplate', { static: true }) shortcutDropdownViewTemplate!: TemplateRef<any>;
    @ViewChild('pagePerformanceProfileLocationTemplate', { static: true }) pagePerformanceProfileLocationTemplate!: TemplateRef<any>;

    // Component state
    search: string = '';
    selectedValues: SelectorOption[] = [];
    highlighted: number = 0;
    isOpen: boolean = false;
    loading: boolean = false;
    filteredOptions: SelectorOption[] = [];
    showCreateOption: boolean = false;
    resolvedViewItemTemplate: TemplateRef<any> | null = null;
    resolvedDropdownGroupTemplate: TemplateRef<any> | null = null;
    resolvedDropdownTemplate: TemplateRef<any> | null = null;
    firstChange = true;

    // Key codes
    private readonly KEYS = {
        up: 38, down: 40, left: 37, right: 39, escape: 27, enter: 13,
        backspace: 8, delete: 46, shift: 16, leftCmd: 91, rightCmd: 93,
        ctrl: 17, alt: 18, tab: 9,
    };

    private searchSubject = new Subject<string>();
    private destroy$ = new Subject<void>();

    // ControlValueAccessor properties
    private onChange = (value: any) => {};
    private onTouched = () => {};
    private clickOutsideHandler?: (event: Event) => void;

    private optionsIterableDiff;

    constructor (
        private elementRef: ElementRef,
        private cdRef: ChangeDetectorRef,
        private iterableDiffers: IterableDiffers,
    ) {
        this.optionsIterableDiff = this.iterableDiffers.find(this.options).create();
    }

    ngOnInit () {
        this.resolvedViewItemTemplate = this.resolveTemplate(this.viewItemTemplate || '');
        this.resolvedDropdownGroupTemplate = this.resolveTemplate(this.dropdownGroupTemplate || '');
        this.resolvedDropdownTemplate = this.resolveTemplate(this.dropdownItemTemplate || '');

        this.initialize();
        this.setupSearch();
        this.setupAPI();
        this.setupClickOutside();

        if (this.remote) {
            this.fetch();
        }
    }

    ngOnChanges (changes) {
        this.setInputWidth();
    }

    ngDoCheck () {
        if (this.optionsIterableDiff.diff(this.options)) {
            this.filterOptions();
        }
    }

    ngOnDestroy () {
        this.destroy$.next();
        this.destroy$.complete();

        // Clean up click outside listener
        if (this.clickOutsideHandler) {
            document.removeEventListener('click', this.clickOutsideHandler);
        }
    }

    private initialize () {
        if (!this.value && this.multiple) {
            this.value = [];
        }

        if (this.hasValue()) {
            if (!this.multiple) {
                if (Array.isArray(this.value)) {
                    this.value = this.value[0];
                }
            } else {
                if (!Array.isArray(this.value)) {
                    this.value = [this.value];
                }
            }
            this.updateSelected();
            this.filterOptions();
        }
    }

    private setupSearch () {
        this.searchSubject.pipe(
            debounceTime(this.debounce),
            switchMap(search => this.performSearch(search)),
        ).subscribe();
    }

    private setupAPI () {
        this.api.open = () => this.open();
        this.api.close = () => this.close();
        this.api.fetch = () => this.fetch();
        this.api.focus = () => this.inputElement.nativeElement.focus();
        this.api.set = (value: any) => this.value = value;
        this.api.unset = (value?: any) => this.unsetValue(value);
    }

    private setupClickOutside () {
        this.clickOutsideHandler = (event: Event) => {
            if (this.isOpen && !this.elementRef.nativeElement.contains(event.target as Node)) {
                this.close();
            }
        };
        document.addEventListener('click', this.clickOutsideHandler);
    }

    private performSearch (search: string): Observable<any> {
        if (this.remote) {
            return this.request('search', search || '', this.remote, this.remoteParam);
        }
        if (this.skipModel) {
            this.filterOptionsOnSkipModel();
            this.cdRef.detectChanges();
        }
        return of(null);
    }

    // Utility methods
    getObjValue (obj: any, path?: string): any {
        if (!obj || !path) {
            return obj;
        }
        const keys = Array.isArray(path) ? path : path.split('.');
        let result = obj;

        for (const key of keys) {
            if (key.indexOf('[') > 0) {
                const match = key.match(/(\w+)\[(\d+)\]/);
                if (match) {
                    result = result[match[1]][match[2]];
                }
            } else {
                result = result[key];
            }
        }

        return result;
    }

    setObjValue (obj: any, path: string, value: any): any {
        if (!obj) {
            obj = {};
        }
        const keys = Array.isArray(path) ? path : path.split('.');
        let current = obj;

        for (let i = 0; i < keys.length - 1; i++) {
            const key = keys[i];
            if (key.indexOf('[') > 0) {
                const match = key.match(/(\w+)\[(\d+)\]/);
                if (match) {
                    current = current[match[1]][match[2]];
                }
            } else {
                if (!current[key]) {
                    current[key] = {};
                }
                current = current[key];
            }
        }

        const lastKey = keys[keys.length - 1];
        current[lastKey] = value;
        return obj;
    }

    optionValue (option: SelectorOption): any {
        return this.valueAttr ? this.getObjValue(option, this.valueAttr) : option;
    }

    optionEquals (option: SelectorOption, value?: any): boolean {
        const optVal = this.optionValue(option);
        const compareVal = value !== undefined ? value : this.value;
        return deepEqual(optVal, compareVal);
    }

    hasValue (): boolean {
        return this.multiple ? (this.value || []).length > 0 : (this.value !== null && this.value !== undefined && this.value !== '');
    }

    // Remote fetching
    request (paramName: string, paramValue: any, remote: any, remoteParam: string): Observable<any> {
        if (this.disabled) {
            return of(null);
        }
        if (!remote) {
            throw new Error('Remote attribute is not defined');
        }

        this.loading = true;
        this.options = [];

        const remoteOptions: RemoteOptions = {};
        remoteOptions[paramName] = paramValue;

        const promise = remote;

        // The promise.then is always guaranteed to be a function, no need to handle $http case
        const request$ = promise;

        return request$.then(data => {
            this.options = data.data || data;
            this.updateSelected();
            this.filterOptions();
            this.updateValue();
            this.loading = false;
            this.cdRef.detectChanges();
        });
    }

    fetch (): Observable<any> {
        return this.request('search', this.search || '', this.remote, this.remoteParam);
    }

    // Dropdown utilities
    open () {
        if (this.multiple && (this.selectedValues || []).length >= this.limit) {
            return;
        }
        this.isOpen = true;
        setTimeout(() => this.scrollToHighlighted());
    }

    close () {
        this.isOpen = false;
        this.resetInput();
        if (this.remote) {
            setTimeout(() => this.fetch());
        }
    }

    decrementHighlighted () {
        this.highlight(this.highlighted - 1);
        this.scrollToHighlighted();
    }

    incrementHighlighted () {
        this.highlight(this.highlighted + 1);
        this.scrollToHighlighted();
    }

    highlight (index: number) {
        const length = this.filteredOptions.length;
        if (length) {
            this.highlighted = (length + index) % length;
        }
        if (this.showCreateOption) {
            if (index === -1 || index === length) {
                this.highlighted = -1;
            }
            if (index === -2) {
                this.highlighted = length - 1;
            }
        }
    }

    scrollToHighlighted () {
        const dropdown = this.dropdown.nativeElement;
        const index = this.highlighted + (this.showCreateOption ? 1 : 0);
        const option = dropdown.querySelectorAll('li.selector-option')[index] as HTMLElement;

        if (!this.filteredOptions.length || !option) {
            return;
        }

        const optionRect = option.getBoundingClientRect();
        const dropdownRect = dropdown.getBoundingClientRect();

        if (optionRect.bottom > dropdownRect.bottom) {
            dropdown.scrollTop = option.offsetTop + option.offsetHeight - dropdown.offsetHeight;
        }

        if (optionRect.top < dropdownRect.top) {
            dropdown.scrollTop = option.offsetTop;
        }
    }

    // Option management
    createOption (value: string) {
        let option: SelectorOption = {};

        if (typeof this.create === 'function') {
            option = this.create({ input: value });
        } else {
            this.setObjValue(option, this.labelAttr, value);
            this.setObjValue(option, this.valueAttr || 'value', value);
        }

        this.options.push(option);
        this.cdRef.detectChanges();
        this.set(option);
    }

    set (option?: SelectorOption) {
        if (this.multiple && (this.selectedValues || []).length >= this.limit) {
            return;
        }

        if (!option) {
            option = this.filteredOptions[this.highlighted];
        }

        if (!this.multiple) {
            this.selectedValues = [option];
        } else {
            if (!this.selectedValues) {
                this.selectedValues = [];
            }
            if (this.selectedValues.indexOf(option) < 0) {
                const newSelectedValues = safeClone(this.selectedValues);
                newSelectedValues.push(option);
                this.selectedValues = newSelectedValues;
                this.cdRef.detectChanges();
            }
        }

        if (!this.multiple || this.closeAfterSelection || (this.selectedValues || []).length >= this.limit) {
            this.close();
        }

        this.resetInput();
        if (this.multiple) {
            this.filterOptions(); // Refresh available options on multiple select
        }
        this.updateValue();
    }

    unset (index?: number) {
        if (!this.multiple) {
            this.selectedValues = [];
        } else {
            const removeIndex = index !== undefined ? index : this.selectedValues.length - 1;
            const newSelectedValues = safeClone(this.selectedValues);
            newSelectedValues.splice(removeIndex, 1);
            this.selectedValues = newSelectedValues;
            this.cdRef.detectChanges();
        }
        this.resetInput();
        this.filterOptions(); // Refresh the available options
        this.updateValue();
    }

    private unsetValue (value?: any) {
        if (!value) {
            this.selectedValues = [];
            this.filterOptions(); // Refresh the available options
        } else {
            const valuesToRemove = this.selectedValues.filter(option => this.optionEquals(option, value));
            const indexes = this.selectedValues.map((option, index) =>
                valuesToRemove.includes(option) ? index : -1,
            ).filter(index => index >= 0);

            indexes.forEach((index, i) => {
                this.unset(index - i);
            });
        }
    }

    // Input handling
    onKeydown (event: KeyboardEvent) {
        switch (event.keyCode) {
            case this.KEYS.up:
                if (!this.isOpen) {
                    this.open();
                } else {
                    this.decrementHighlighted();
                }
                event.preventDefault();
                break;

            case this.KEYS.down:
                if (!this.isOpen) {
                    this.open();
                } else {
                    this.incrementHighlighted();
                }
                event.preventDefault();
                break;

            case this.KEYS.escape:
                this.highlight(0);
                this.close();
                break;

            case this.KEYS.enter:
                if (this.isOpen) {
                    if (this.create && this.search && this.highlighted === -1) {
                        this.createOption(this.search);
                    } else if (this.filteredOptions.length) {
                        this.set();
                    }
                    event.preventDefault();
                }
                break;

            case this.KEYS.backspace:
                if (!this.search) {
                    const lastSelected = this.selectedValues[this.selectedValues.length - 1];
                    const searchValue = this.getObjValue(lastSelected || {}, this.labelAttr || '');
                    this.unset();
                    this.open();
                    if (this.softDelete && !this.disableSearch) {
                        setTimeout(() => this.search = searchValue);
                    }
                    event.preventDefault();
                }
                break;

            case this.KEYS.left:
            case this.KEYS.right:
            case this.KEYS.shift:
            case this.KEYS.ctrl:
            case this.KEYS.alt:
            case this.KEYS.leftCmd:
            case this.KEYS.rightCmd:
                break;

            case this.KEYS.tab: {
                this.close();
                break;
            }

            default: {
                const hasValue = this.hasValue();
                if (!this.multiple && hasValue) {
                    event.preventDefault();
                } else {
                    this.open();
                    this.highlight(0);
                }

                break;
            }
        }
    }

    onSearchChange (value: string) {
        this.search = value;
        this.searchSubject.next(value);
        this.filterOptions();
        this.setInputWidth();
    }

    onFocus () {
        this.open();
    }

    onBlur () {
        this.onTouched();
    }

    onInputClick () {
        this.inputElement.nativeElement.focus();
    }

    filterOptionsOnSkipModel () {
        if (!this.options) {
            return;
        }

        this.filteredOptions = this.options.filter(option => {
            const label = this.getObjValue(option, this.labelAttr) || '';
            return label.toLowerCase().includes((this.search || '').toLowerCase());
        });
    }

    // Filtering
    filterOptions () {
        if (!this.options) {
            return;
        }

        this.filteredOptions = this.options.filter(option => {
            const label = this.getObjValue(option, this.labelAttr) || '';
            return label.toLowerCase().includes((this.search || '').toLowerCase());
        });

        if (!this.selectedValues) {
            this.selectedValues = [];
        }

        if (this.multiple) {
            this.filteredOptions = this.filteredOptions.filter(option =>
                !this.selectedValues.some(selected => this.optionEquals(selected, this.optionValue(option))),
            );
        } else {
            const index = this.filteredOptions.findIndex(option =>
                this.selectedValues.length > 0 && this.optionEquals(option, this.optionValue(this.selectedValues[0])),
            );
            if (index >= 0) {
                this.highlight(index);
            }
        }

        // Show create option
        if (this.create) {
            this.showCreateOption = !!(this.search &&
        !this.filteredOptions.some(option => {
            const label = this.getObjValue(option, this.labelAttr) || '';
            return label.toLowerCase() === this.search.toLowerCase();
        })
            );

            if (this.showCreateOption && this.filteredOptions.length === 0 && this.highlighted !== -1) {
                this.highlighted = -1;
            }
        }
    }

    // Value management
    updateSelected () {
        if (!this.multiple) {
            this.selectedValues = (this.options || []).filter(option =>
                this.optionEquals(option, this.value),
            ).slice(0, 1);
        } else {
            this.selectedValues = (this.value || []).map((value: any) =>
                this.options.find(option => this.optionEquals(option, value)),
            ).filter((value: any) => value !== undefined).slice(0, this.limit);
        }
        this.setInputWidth();
    }

    updateValue () {
        const origin = this.selectedValues || [];
        if (!this.multiple) {
            this.value = this.valueAttr ? this.getObjValue(origin[0] || {}, this.valueAttr) : origin[0];
        } else {
            this.value = this.valueAttr ? origin.map(option => this.getObjValue(option, this.valueAttr)) : origin;
        }


        // Notify Angular forms of the change
        this.onChange(Array.isArray(this.value) ? safeClone(this.value) : this.value);

        this.change.emit({
            newValue: this.multiple ? this.selectedValues.map(val => this.optionValue(val)) : this.optionValue(this.selectedValues[0]),
            oldValue: this.multiple ? [] : undefined,
            firstChange: this.firstChange,
        });
        if (this.firstChange) {
            this.firstChange = false;
        }
    }

    resetInput () {
        this.search = '';
        this.setInputWidth();
    }

    hasOption (): boolean {
        return !!this.options.find(option => this.optionEquals(option, this.value));
    }

    private setInputWidth () {
        const input = this.inputElement.nativeElement;
        if (!this.isOpen && !this.search && this.selectedValues.length === 0) {
            input.style.width = '100%';
            return;
        }
        if (!this.isOpen && this.skipModel) {
            input.style.width = '100%';
            return;
        }
        if (this.skipModel && this.options.length === 0) {
            input.style.width = '100%';
            return;
        }
        const width = this.measureWidth() + 1;
        input.style.width = width + 'px';
    }

    private measureWidth (): number {
        if (!this.isOpen && !this.search) {
            return 0;
        }

        const input = this.inputElement.nativeElement;
        const styles = window.getComputedStyle(input);
        const shadowElement = document.createElement('span');

        shadowElement.className = 'selector-shadow';
        shadowElement.textContent = this.search || (!this.hasValue() ? this.placeholder : '') || '';
        document.body.appendChild(shadowElement);

        const styleProps = ['fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'letterSpacing', 'textTransform', 'wordSpacing', 'textIndent'];
        styleProps.forEach(prop => {
            (shadowElement.style as any)[prop] = (styles as any)[prop];
        });

        const width = shadowElement.offsetWidth;
        document.body.removeChild(shadowElement);

        return width;
    }

    trackByFn (index: number, item: any): any {
        return index;
        // return item ? this.optionValue(item) : index;
    }

    // ControlValueAccessor methods
    writeValue (value: any): void {
        this.value = value;
        if (this.hasValue()) {
            this.updateSelected();
            this.filterOptions();
        } else {
            this.selectedValues = [];
        }
    }

    registerOnChange (fn: (value: any) => void): void {
        this.onChange = fn;
    }

    registerOnTouched (fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState (isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    resolveTemplate (templateUrl: string) {
        switch (templateUrl) {
            case 'app/forms/domain/steps/features/dropdown-templates/sce-select.html':
                return this.sceSelectTemplate;
            case 'app/forms/domain/steps/features/dropdown-templates/sce-dropdown-item.html':
                return this.sceDropdownItemTemplate;
            case 'app/modules/page-performance/components/profile-dropdown/profile-dropdown-templates/profile-select-item.html':
                return this.profileSelectItemTemplate;
            case 'app/modules/page-performance/components/profile-dropdown/profile-dropdown-templates/profile-select.html':
                return this.profileSelectTemplate;
            case 'app/modules/page-performance/forms/page/templates/profile_template.html':
                return this.profileTemplate;
            case 'app/modules/source-code-excludes/form/source-code-exclude/modules/templates/dropdown-template.html':
                return this.sceDropdownTemplate;
            case 'app/pages/global/changes-history/dropdown-templates/domain-dropdown-item.html':
                return this.domainDropdownItemTemplate;
            case 'app/pages/global/changes-history/dropdown-templates/domain-select.html':
                return this.domainSelectTemplate;
            case 'app/pages/global/changes-history/dropdown-templates/module-select.html':
                return this.moduleSelectTemplate;
            case 'app/modules/shortcut/forms/dropdown-templates/dropdown-item.html':
                return this.shortcutDropdownItemTemplate;
            case 'app/modules/shortcut/forms/dropdown-templates/view.html':
                return this.shortcutDropdownViewTemplate;
            case 'app/modules/page-performance/forms/profile/dropdown-templates/location-item.html':
                return this.pagePerformanceProfileLocationTemplate;
            default:
                return null;
        }
    }
}
